"use client";
import React, {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "./index.scss";

interface TabbedContentChildProps {
  title: string;
  id: string;
  children: React.ReactNode;
}

interface TabbedContentProps {
  children: ReactElement<TabbedContentChildProps>[];
  className?: string;
}

const TabbedContent: React.FC<TabbedContentProps> = ({
  children,
  className,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const hasScrolledRef = useRef(false);
  const tabWrapperRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false); 

  // Handle hash on initial load
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const tabIndex = children.findIndex((child) => child.props.id === hash);

    if (tabIndex >= 0) {
      setSelectedIndex(tabIndex);
      shouldScrollRef.current = true; 
    }
  }, [children]);

  // Scroll to top of tab component after selection
  useLayoutEffect(() => {
    if (
      shouldScrollRef.current &&
      !hasScrolledRef.current &&
      tabWrapperRef.current
    ) {
      const offset = 110; 
      const top =
        tabWrapperRef.current.getBoundingClientRect().top +
        window.scrollY -
        offset;
      window.scrollTo({ top, behavior: "smooth" });

      hasScrolledRef.current = true;
      shouldScrollRef.current = false; 
    }
  }, [selectedIndex]);

  const handleTabSelect = (index: number) => {
    setSelectedIndex(index);
    window.location.hash = children[index].props.id;
    hasScrolledRef.current = false; 
  };

  return (
    <div ref={tabWrapperRef}>
      <Tabs
        className={`component tabbed-content fade-in ${className || ""}`}
        selectedIndex={selectedIndex}
        onSelect={handleTabSelect}
      >
        <TabList>
          {React.Children.map(children, (child) => (
            <Tab>{child.props.title}</Tab>
          ))}
        </TabList>

        {React.Children.map(children, (child) => (
          <TabPanel>{child}</TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

export default TabbedContent;
